import React, { forwardRef } from 'react';
import { theme, expandFontTheme, useThemeContext, getThemeColor, } from '@contrast-security-inc/design-system-foundations';
import { css, cx } from '@emotion/css';
const baseStyles = css `
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  color: ${theme.color.neutral100};
  min-width: 60px;
  max-width: 220px;
  justify-content: center;
  outline-offset: -1px;
  ${expandFontTheme(theme.font.body)};

  &:disabled {
    opacity: 0.3;
    cursor: default;
  }

  &:focus-visible:enabled {
    outline: 1px solid ${theme.color.neutral100};
  }

  &:hover:enabled,
  &:hover:enabled svg {
    color: ${theme.color.white};
    fill: ${theme.color.white};
  }
`;
const primaryStyles = css `
  background-color: ${theme.color.teal50};

  &:hover:enabled {
    background-color: ${theme.color.turquoise90};
  }
`;
const secondaryStyles = (selectedTheme) => css `
  background-color: ${getThemeColor(selectedTheme, { light: theme.color.white, dark: theme.color.black })};
  color: ${getThemeColor(selectedTheme, { light: theme.color.neutral100, dark: theme.color.neutral10 })};
  outline: 1px solid ${getThemeColor(selectedTheme, { light: theme.color.neutral50, dark: theme.color.neutral70 })};

  &:hover:enabled {
    background-color: ${getThemeColor(selectedTheme, { light: theme.color.neutral70, dark: theme.color.neutral80 })};
    outline: 1px solid ${getThemeColor(selectedTheme, { light: theme.color.neutral70, dark: theme.color.neutral80 })};
  }

  &:focus-visible:enabled {
    outline: 1px solid ${theme.color.neutral100};
  }

  &:focus-visible:enabled,
  &:focus-visible:enabled svg {
    color: ${theme.color.turquoise90};
    fill: ${theme.color.turquoise90};
  }

  &:hover:focus-visible:enabled,
  &:hover:enabled svg {
    color: ${theme.color.white};
    fill: ${theme.color.white};
  }
`;
const destructiveStyles = css `
  background-color: ${theme.color.red30};
  color: ${theme.color.black};

  &:focus-visible:enabled {
    outline: 1px solid ${theme.color.black};
  }

  &:hover:enabled {
    background-color: ${theme.color.red70};
  }
`;
const iconStyles = css `
  display: flex;
  padding-right: 4px;
`;
const textOverflowStyles = css `
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
`;
const getStyles = (variant, selectedTheme) => {
    switch (variant) {
        case 'primary':
            return primaryStyles;
        case 'secondary':
            return secondaryStyles(selectedTheme);
        case 'destructive':
            return destructiveStyles;
    }
};
export const Button = forwardRef(({ testId, className, variant = 'primary', isDisabled, Icon, type = 'button', children, ...rest }, ref) => {
    const { selectedTheme } = useThemeContext();
    const classes = cx(baseStyles, getStyles(variant, selectedTheme), className, 'ds-button');
    return (React.createElement("button", { className: classes, "data-testid": testId, ref: ref, type: type, ...rest, disabled: isDisabled },
        Icon && (React.createElement("span", { "data-testid": `${testId}-icon`, className: iconStyles },
            React.createElement(Icon, { size: "sm", color: "neutral100" }))),
        React.createElement("div", { className: textOverflowStyles }, children)));
});
Button.displayName = 'Button';
